import { TileInformationDataList, TileInformationDataListItemStat, TileInformationDataListItemStatType } from "@/api/dtos";
import Icon from "@/components/layouts/Icon/Icon";
import type { SystemStyleObject } from "@mui/system";
import { Box, Chip, Divider, ListItemText, type Theme } from "@mui/material";
import { ComponentType, FC, Fragment } from "react";
import { ITileInformationDataComponentProps } from "..";
import { StyledList, StyledListItem, StyledListItemIcon } from "../..";

interface ITileInformationDataListItemStatComponentProps {
    stat: TileInformationDataListItemStat;
}

const STAT_MIN_WIDTH = '45px';

const statBackgroundColor = (stat: TileInformationDataListItemStat): SystemStyleObject<Theme> => ({
    backgroundColor: (theme) => theme.palette.mode === 'dark'
        ? stat.backgroundColorDark
        : stat.backgroundColorLight,
});

const TileInformationDataListItemStatChip: FC<ITileInformationDataListItemStatComponentProps> = ({ stat }) => {
    return (
        <Chip
            sx={[
                statBackgroundColor(stat),
                { minWidth: STAT_MIN_WIDTH }
            ]}
            label={stat.value}
            size="small"
        />
    )
}

const TileInformationDataListItemStatText: FC<ITileInformationDataListItemStatComponentProps> = ({ stat }) => {
    return (
        <Box
            sx={statBackgroundColor(stat)}
        >
            {stat.value}
        </Box>
    )
}


const TileInformationDataListItemStatMapping: Record<TileInformationDataListItemStatType, ComponentType<ITileInformationDataListItemStatComponentProps>> = {
    [TileInformationDataListItemStatType.Chip]: TileInformationDataListItemStatChip,
    [TileInformationDataListItemStatType.Text]: TileInformationDataListItemStatText
}

const TileInformationDataListComponent: FC<ITileInformationDataComponentProps<TileInformationDataList>> = ({ data }) => {

    return (
        <StyledList>
            {
                data.items?.map((item, i, entireList) => (
                    <Fragment key={i}>
                        <StyledListItem>
                            {
                                item.iconPrefix && item.iconName && (
                                    <StyledListItemIcon
                                        sx={{
                                            color: (theme) => theme.palette.mode === 'dark' ? 
                                                item.iconColorDark : 
                                                item.iconColorLight
                                        }}
                                    >
                                        <Icon icon={{ prefix: item.iconPrefix as any, iconName: item.iconName as any }} size="lg" fixedWidth />
                                    </StyledListItemIcon>
                                )
                            }
                            <ListItemText 
                                primary={item.title && (
                                    <Box
                                        sx={{
                                            color: (theme) => theme.palette.mode === 'dark' ? 
                                                item.titleColorDark : 
                                                item.titleColorLight
                                        }}
                                    >
                                        {item.title}
                                    </Box>
                                )}
                                secondary={item.subtitle && (
                                    <Box
                                        sx={{
                                            color: (theme) => theme.palette.mode === 'dark' ? 
                                                item.subtitleColorDark : 
                                                item.subtitleColorLight
                                        }}
                                    >
                                        {item.subtitle}
                                    </Box>
                                )}
                            />
                            {
                                item.stats && item.stats.length > 0 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 2,
                                            alignItems: 'center',
                                        }}
                                    >
                                        {
                                            item.stats.map((stat, j) => {
                                                const Component = TileInformationDataListItemStatMapping[stat.type];

                                                return (
                                                    <Component key={j} stat={stat} />
                                                )
                                            })
                                        }
                                    </Box>
                                )
                            }
                        </StyledListItem>
                        {
                            i < entireList.length - 1 && <Divider />
                        }
                    </Fragment>
                ))
            }
        </StyledList>
    )
}

export default TileInformationDataListComponent;